import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { 
  TextField, Button, Typography, Box, Container, Alert,
  CircularProgress, Checkbox, FormControlLabel, InputAdornment, 
  IconButton, Paper, Divider, Select, MenuItem, FormControl,
  InputLabel, Chip, Stack, Tooltip, Card, CardContent
} from '@mui/material';
import { styled } from '@mui/system';
import { 
  Visibility, VisibilityOff, Google, Facebook, KeyOutlined,
  Business, WorkOutline, LocationCity, Store
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Styled components (keeping your existing styles)
const StyledPaper = styled(Paper)(({ theme }) => ({
  marginTop: theme?.spacing(8) || '64px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme?.spacing(4) || '32px',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
}));

const StyledForm = styled('form')({
  width: '100%',
  marginTop: '16px',
  '& > *': {
    marginBottom: '16px !important'
  }
});

const SocialButton = styled(Button)(({ theme }) => ({
  margin: theme?.spacing(1, 0) || '8px 0',
  '&.MuiButton-containedGoogle': {
    backgroundColor: '#db4437',
    '&:hover': {
      backgroundColor: '#c23321'
    }
  },
  '&.MuiButton-containedFacebook': {
    backgroundColor: '#3b5998',
    '&:hover': {
      backgroundColor: '#2d4373'
    }
  }
}));

const ContextCard = styled(Card)(({ theme }) => ({
  marginBottom: '16px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '& .MuiCardContent-root': {
    padding: '12px 16px',
    '&:last-child': {
      paddingBottom: '12px'
    }
  }
}));

const Login = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Branch/Department selection state
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showBranchDeptSelection, setShowBranchDeptSelection] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);

  const {
    login,
    complete2FALogin,
    requires2FA,
    switchBranch,
    switchDepartment,
    availableBranches,
    availableDepartments,
    currentBranch,
    currentDepartment,
    isCrossBranch,
    isCrossDepartment,
    user
  } = useAuth();

  const navigate = useNavigate();

  // Load user's branch/department context after successful login
  useEffect(() => {
    if (user && (isCrossBranch || isCrossDepartment)) {
      setShowBranchDeptSelection(true);
    }
  }, [user, isCrossBranch, isCrossDepartment]);

  // Auto-select defaults if user has single branch/department
  useEffect(() => {
    if (user && availableBranches.length === 1 && !selectedBranch) {
      setSelectedBranch(availableBranches[0].id);
    }
    if (user && availableDepartments.length === 1 && !selectedDepartment) {
      setSelectedDepartment(availableDepartments[0].id);
    }
  }, [availableBranches, availableDepartments, user, selectedBranch, selectedDepartment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = "Username is required";
    if (!formData.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validate2FAForm = () => {
    const newErrors = {};
    if (!twoFactorCode) newErrors.twoFactorCode = "Verification code is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBranchDeptSelection = () => {
    const newErrors = {};
    if (isCrossBranch && !selectedBranch) {
      newErrors.branch = "Please select a branch to continue";
    }
    if (isCrossDepartment && !selectedDepartment) {
      newErrors.department = "Please select a department to continue";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) return;

    setLoading(true);
    try {
      // FIXED: Don't pass rememberMe to login function - handle it separately
      const result = await login(formData.username, formData.password);

      // Handle rememberMe functionality after successful login
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('rememberedUsername', formData.username);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberedUsername');
      }

      // If 2FA is not required and user doesn't need branch/dept selection, redirect
      if (!result?.requires2FA && !showBranchDeptSelection) {
        navigate('/dashboard');
      }
      // If 2FA is required, the UI will update based on requires2FA state
      // If branch/dept selection is needed, showBranchDeptSelection will be true
    } catch (error) {
      setErrors({ general: 'Failed to log in. Please check your credentials.' });
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate2FAForm()) return;

    setLoading(true);
    try {
      await complete2FALogin(twoFactorCode, useBackupCode);

      // After successful 2FA, check if branch/dept selection is needed
      if (!showBranchDeptSelection) {
        navigate('/dashboard');
      }
      // Otherwise, the branch/dept selection form will show
    } catch (error) {
      setErrors({
        general: useBackupCode
          ? 'Invalid backup code. Please try again.'
          : 'Invalid verification code. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBranchDeptSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateBranchDeptSelection()) return;

    setLoading(true);
    try {
      // Switch to selected branch if cross-branch user
      if (isCrossBranch && selectedBranch && selectedBranch !== currentBranch?.id) {
        await switchBranch(parseInt(selectedBranch));
      }

      // Switch to selected department if cross-department user
      if (isCrossDepartment && selectedDepartment && selectedDepartment !== currentDepartment?.id) {
        await switchDepartment(parseInt(selectedDepartment));
      }

      // Navigate to dashboard after successful context setup
      navigate('/dashboard');
    } catch (error) {
      setErrors({
        general: 'Failed to set up your workspace context. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleUseBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setTwoFactorCode('');
    setErrors({});
  };

  const handleSocialLogin = (provider) => {
    // Implement social login logic here
    console.log(`Login with ${provider}`);
  };

  const getCurrentStep = () => {
    if (requires2FA) return '2fa';
    if (showBranchDeptSelection && user) return 'context';
    return 'login';
  };

  const renderContextSelection = () => (
    <StyledForm onSubmit={handleBranchDeptSubmit}>
      <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
        Select Your Workspace
      </Typography>

      {/* Current Context Display */}
      {(currentBranch || currentDepartment) && (
        <ContextCard>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Current Context
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {currentBranch && (
                <Chip
                  icon={<Business />}
                  label={`${currentBranch.name} (${currentBranch.code})`}
                  size="small"
                  sx={{ color: 'white', borderColor: 'white' }}
                  variant="outlined"
                />
              )}
              {currentDepartment && (
                <Chip
                  icon={<WorkOutline />}
                  label={`${currentDepartment.name} (${currentDepartment.code})`}
                  size="small"
                  sx={{ color: 'white', borderColor: 'white' }}
                  variant="outlined"
                />
              )}
            </Stack>
          </CardContent>
        </ContextCard>
      )}

      {/* Branch Selection */}
      {isCrossBranch && availableBranches.length > 0 && (
        <FormControl fullWidth error={!!errors.branch}>
          <InputLabel>Select Branch</InputLabel>
          <Select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            label="Select Branch"
            startAdornment={
              <InputAdornment position="start">
                <LocationCity />
              </InputAdornment>
            }
          >
            {availableBranches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {branch.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {branch.code} • {branch.city}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.branch && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {errors.branch}
            </Typography>
          )}
        </FormControl>
      )}

      {/* Department Selection */}
      {isCrossDepartment && availableDepartments.length > 0 && (
        <FormControl fullWidth error={!!errors.department}>
          <InputLabel>Select Department</InputLabel>
          <Select
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            label="Select Department"
            startAdornment={
              <InputAdornment position="start">
                <Store />
              </InputAdornment>
            }
          >
            {availableDepartments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkOutline fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {department.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {department.code} • {department.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.department && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {errors.department}
            </Typography>
          )}
        </FormControl>
      )}

      {errors.general && <Alert severity="error">{errors.general}</Alert>}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={loading}
        sx={{ mt: 2, mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Continue to Dashboard'}
      </Button>

      {/* Skip option for users who can use default context */}
      {!isCrossBranch && !isCrossDepartment && (
        <Button
          fullWidth
          variant="text"
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 1 }}
        >
          Continue with Default Settings
        </Button>
      )}
    </StyledForm>
  );

  return (
    <Container
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      maxWidth="sm"
    >
      <StyledPaper elevation={3}>
        <Typography component="h1" variant="h4" gutterBottom>
          {getCurrentStep() === '2fa' && 'Two-Factor Authentication'}
          {getCurrentStep() === 'context' && 'Workspace Setup'}
          {getCurrentStep() === 'login' && 'Sign in'}
        </Typography>

        {getCurrentStep() === '2fa' ? (
          // 2FA Verification Form
          <StyledForm onSubmit={handle2FASubmit}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Tabs value={useBackupCode ? 1 : 0} onChange={() => toggleUseBackupCode()}>
                <Tab label="Verification Code" />
                <Tab label="Backup Code" />
              </Tabs>
            </Box>

            <TextField
              name="twoFactorCode"
              label={useBackupCode ? "Backup Code" : "Verification Code"}
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              error={!!errors.twoFactorCode}
              helperText={errors.twoFactorCode}
              fullWidth
              required
              autoFocus
              placeholder={useBackupCode ? "Enter your backup code" : "Enter the 6-digit code"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyOutlined />
                  </InputAdornment>
                ),
              }}
            />

            {errors.general && <Alert severity="error">{errors.general}</Alert>}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 2, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 2 }}>
              <Button
                variant="text"
                onClick={toggleUseBackupCode}
                size="small"
              >
                {useBackupCode
                  ? "Use verification code instead"
                  : "Use backup code instead"}
              </Button>
            </Box>
          </StyledForm>
        ) : getCurrentStep() === 'context' ? (
          // Branch/Department Context Selection
          renderContextSelection()
        ) : (
          // Regular Login Form
          <>
            <StyledForm onSubmit={handleSubmit}>
              <TextField
                name="username"
                label="Username"
                value={formData.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">@</InputAdornment>,
                }}
              />
              <TextField
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />}
                label="Remember me"
              />
              {errors.general && <Alert severity="error">{errors.general}</Alert>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mt: 2, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
                <Link to="/forgot-password">Forgot password?</Link>
                <Link to="/register">Don't have an account? Sign Up</Link>
              </Box>
            </StyledForm>
            <Divider sx={{ my: 2, width: '100%' }}>OR</Divider>
            <Box sx={{ width: '100%' }}>
              <SocialButton
                fullWidth
                variant="contained"
                startIcon={<Google />}
                onClick={() => handleSocialLogin('Google')}
                className="MuiButton-containedGoogle"
              >
                Sign in with Google
              </SocialButton>
              <SocialButton
                fullWidth
                variant="contained"
                startIcon={<Facebook />}
                onClick={() => handleSocialLogin('Facebook')}
                className="MuiButton-containedFacebook"
              >
                Sign in with Facebook
              </SocialButton>
            </Box>
          </>
        )}
      </StyledPaper>
    </Container>
  );
};

export default Login;
